import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthProvider";
import { useAuthenticatedFetch } from "../Keycloak/useAuthenticatedFetch";

interface WorkspaceData {
  longName: string;
  sender?: string;
  onMaintenance?: number;
  [key: string]: string | number | boolean | undefined;
}

export interface UserInfo {
  userId: string;
  name?: string;
  username?: string;
  email?: string;
  availableActifsWorkspace?: { [key: string]: WorkspaceData };
  availableActifsForAllWorkspaces?: { [key: string]: WorkspaceData };
  [key: string]: string | number | boolean | null | undefined | { [key: string]: WorkspaceData };
}

const useGetUserInfo = (shouldFetch: boolean = true) => {
  const [data, setData] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { keycloakInstance, isLoading: authLoading } = useAuth();
  const authFetch = useAuthenticatedFetch();

  const fetchUserInfo = useCallback(async (): Promise<void> => {
    if (!keycloakInstance?.authenticated) return;

    setLoading(true);
    setError("");
    try {
      const result = await authFetch("/me", { method: "GET" }) as UserInfo;
      setData(result);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Erreur inconnue lors du chargement du profil utilisateur.";
      console.error("[useGetUserInfo] fetchUserInfo failed:", message);
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [keycloakInstance, authFetch]);

  useEffect(() => {
    if (authLoading || !shouldFetch) return;
    if (keycloakInstance?.authenticated) {
      fetchUserInfo();
    }
  }, [keycloakInstance, authLoading, shouldFetch, fetchUserInfo]);

  return { data, loading, error, refetch: fetchUserInfo };
};

export default useGetUserInfo;
